import { useState } from 'react';

import { FormField, FormInput, FormSelect } from '@/components/form';

export type TextProvider = 'google' | 'anthropic' | 'openai';
export type TextReasoningEffort = 'low' | 'medium' | 'high';

const CUSTOM_MODEL = '__custom__';

const TEXT_PROVIDER_OPTIONS: ReadonlyArray<{ value: TextProvider; label: string }> = [
  { value: 'google', label: 'Google Gemini' },
  { value: 'anthropic', label: 'Anthropic Claude (via Google Cloud)' },
  { value: 'openai', label: 'OpenAI' },
];

// Prices are USD per 1M input / output tokens.
export const TEXT_MODEL_OPTIONS: Record<
  TextProvider,
  ReadonlyArray<{ value: string; label: string }>
> = {
  google: [
    { value: '', label: 'Gemini 2.5 Flash (default)' },
    { value: 'gemini-3.8-flash', label: 'Gemini 3.8 Flash' },
    { value: 'gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash-Lite — cheapest' },
  ],
  anthropic: [
    { value: 'claude-haiku-5-5', label: 'Claude Haiku 5.5 — fast, $0.10 / $0.50' },
    { value: 'claude-sonnet-5-5', label: 'Claude Sonnet 5.5 — stronger, $2 / $10' },
  ],
  openai: [
    { value: 'gpt-6-luna', label: 'GPT-6 Luna — fast, $0.10 / $0.50' },
    { value: 'gpt-6-sol', label: 'GPT-6 Sol — stronger, $2 / $10' },
  ],
};

const EFFORT_OPTIONS: ReadonlyArray<{ value: TextReasoningEffort; label: string }> = [
  { value: 'low', label: 'Low — fastest replies' },
  { value: 'medium', label: 'Medium (default)' },
  { value: 'high', label: 'High — deepest reasoning, slower' },
];

/** Same rule as the backend: claude-* → Claude, gpt-* / o1… → OpenAI, else Gemini. */
export const textProviderOf = (model: string): TextProvider => {
  const name = model.trim().toLowerCase();
  if (name.startsWith('claude-')) return 'anthropic';
  if (name.startsWith('gpt-') || /^o\d/.test(name)) return 'openai';
  return 'google';
};

const isListed = (provider: TextProvider, model: string): boolean =>
  TEXT_MODEL_OPTIONS[provider].some((option) => option.value === model);

const toEffort = (value: unknown): TextReasoningEffort =>
  value === 'low' || value === 'high' ? value : 'medium';

interface TextModelFieldsProps {
  /** Saved text_llm_model; empty means the Gemini default. */
  model: string;
  /** Saved advanced.text_reasoning_effort. */
  effort: unknown;
  error?: string;
  onModelChange: (model: string) => void;
  onEffortChange: (effort: TextReasoningEffort) => void;
}

/** Provider, model and reasoning effort for WhatsApp, email and web-chat conversations. */
export const TextModelFields = ({
  model,
  effort,
  error,
  onModelChange,
  onEffortChange,
}: TextModelFieldsProps) => {
  const provider = textProviderOf(model);
  const [customOpen, setCustomOpen] = useState(false);
  const showCustom = customOpen || !isListed(provider, model);

  const handleProviderChange = (next: TextProvider) => {
    setCustomOpen(false);
    onModelChange(TEXT_MODEL_OPTIONS[next][0].value);
  };

  const handleModelChange = (value: string) => {
    if (value === CUSTOM_MODEL) {
      setCustomOpen(true);
      return;
    }
    setCustomOpen(false);
    onModelChange(value);
  };

  return (
    <div className="grid gap-6 md:grid-cols-2">
      <FormField
        label="Text messaging provider"
        htmlFor="text_llm_provider"
        description="Used for WhatsApp, email and web-chat conversations. Voice calls are not affected."
      >
        <FormSelect
          id="text_llm_provider"
          value={provider}
          onChange={(event) => handleProviderChange(event.target.value as TextProvider)}
        >
          {TEXT_PROVIDER_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </FormSelect>
      </FormField>

      <FormField label="Text messaging model" htmlFor="text_llm_model_select" error={error}>
        <FormSelect
          id="text_llm_model_select"
          value={showCustom ? CUSTOM_MODEL : model}
          error={!!error}
          onChange={(event) => handleModelChange(event.target.value)}
        >
          {TEXT_MODEL_OPTIONS[provider].map((option) => (
            <option key={option.value || 'default'} value={option.value}>
              {option.label}
            </option>
          ))}
          <option value={CUSTOM_MODEL}>Custom model name…</option>
        </FormSelect>
        {showCustom && (
          <FormInput
            id="text_llm_model"
            aria-label="Custom text model name"
            className="mt-2"
            placeholder="e.g. claude-haiku-5-5, gpt-6-luna, gemini-3.8-flash"
            value={model}
            error={!!error}
            onChange={(event) => onModelChange(event.target.value)}
          />
        )}
      </FormField>

      {provider !== 'google' && (
        <FormField
          label="Reasoning effort"
          htmlFor="text_reasoning_effort"
          description="Lower effort answers faster and costs less; higher effort thinks longer."
        >
          <FormSelect
            id="text_reasoning_effort"
            value={toEffort(effort)}
            onChange={(event) => onEffortChange(event.target.value as TextReasoningEffort)}
          >
            {EFFORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </FormSelect>
        </FormField>
      )}
    </div>
  );
};
